import { Clock, CalendarDays } from "lucide-react";
import { totals } from "./Metrics";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const hourLabel = (h) => `${h % 12 || 12}${h < 12 ? "am" : "pm"}`;
const postTime = (t) => new Date(t.posted_at || t.published_at);

function analyse(items, platform) {
  const day = DAYS.map(() => ({ v: 0, n: 0 }));
  const hour = Array.from({ length: 24 }, () => ({ v: 0, n: 0 }));
  let posts = 0;
  items.forEach((t) => {
    const m = (t.metrics || {})[platform];
    if (!m || !(t.posted_at || t.published_at)) return;
    const d = postTime(t);
    if (Number.isNaN(d.getTime())) return;
    const views = Number(m.views || 0);
    posts += 1;
    const di = (d.getDay() + 6) % 7;
    day[di].v += views; day[di].n += 1;
    hour[d.getHours()].v += views; hour[d.getHours()].n += 1;
  });
  const avg = (x) => (x.n ? Math.round(x.v / x.n) : 0);
  const dayAvg = day.map(avg), hourAvg = hour.map(avg);
  const best = (arr) => (Math.max(...arr) > 0 ? arr.indexOf(Math.max(...arr)) : -1);
  return { posts, dayAvg, hourAvg, bestDay: best(dayAvg), bestHour: best(hourAvg), dayN: day.map((x) => x.n), hourN: hour.map((x) => x.n) };
}

export function suggestSlots(items, platforms) {
  const pub = items.filter((t) => t.stage === "Published" && t.metrics);
  const now = new Date();
  return platforms.map((p) => {
    const a = analyse(pub, p);
    if (a.bestDay < 0) return { platform: p, none: true };
    const d = new Date(now);
    let add = (a.bestDay - ((now.getDay() + 6) % 7) + 7) % 7;
    if (add === 0 && now.getHours() >= a.bestHour) add = 7;
    d.setDate(now.getDate() + add);
    const date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    return { platform: p, posts: a.posts, day: DAYS[a.bestDay], hour: hourLabel(a.bestHour), avg: a.hourAvg[a.bestHour], date, time: `${String(a.bestHour).padStart(2, "0")}:00` };
  });
}

export function SmartSlots({ items, platforms, onPick, testid = "smart-slot" }) {
  if (!platforms?.length) return null;
  const slots = suggestSlots(items, platforms);
  return (
    <div data-testid={testid} className="rounded-lg border border-[#E8D7AE] bg-[#FBF7EC] p-2.5">
      <div className="mb-1.5 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-[#A9792C]"><Clock className="h-3 w-3" />Suggested publish time</div>
      <div className="space-y-1">
        {slots.map((s) => (
          <div key={s.platform} className="flex flex-wrap items-center justify-between gap-2 text-xs">
            {s.none ? <span className="text-zinc-500"><b className="text-zinc-700">{s.platform}</b>: no results logged yet</span> : (
              <>
                <span className="text-zinc-600"><b className="text-zinc-800">{s.platform}</b>: {s.day} {s.hour} · next {new Date(s.date).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" })} · {s.avg.toLocaleString("en-IN")} avg views ({s.posts} post{s.posts === 1 ? "" : "s"})</span>
                <button type="button" data-testid={`${testid}-use-${s.platform.split(" ")[0].toLowerCase()}`} onClick={() => onPick(s.date, s.time)} className="rounded-full bg-zinc-900 px-2.5 py-0.5 text-[11px] font-semibold text-[#E8C877] transition-transform hover:scale-105">Use</button>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function PlatformCard({ platform, a }) {
  const slug = platform.split(" ")[0].toLowerCase();
  const maxD = Math.max(1, ...a.dayAvg), maxH = Math.max(1, ...a.hourAvg);
  return (
    <div data-testid={`best-time-${slug}`} className="rounded-xl border border-zinc-200 bg-white p-4">
      <div className="flex items-center justify-between"><div className="font-semibold text-zinc-900">{platform}</div><span className="text-[11px] text-zinc-400">{a.posts} post{a.posts === 1 ? "" : "s"} with results</span></div>
      {a.bestDay < 0 ? <div className="mt-3 text-xs text-zinc-400">No views logged yet for {platform}.</div> : (
        <>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <div className="rounded-lg bg-[#F6F1E6] p-2.5"><div className="flex items-center gap-1 text-[11px] text-zinc-500"><CalendarDays className="h-3 w-3" />Best day</div><div data-testid={`best-day-${slug}`} className="font-display text-lg font-bold text-zinc-900">{DAYS[a.bestDay]}</div><div className="text-[11px] text-zinc-500">{a.dayAvg[a.bestDay].toLocaleString("en-IN")} avg views</div></div>
            <div className="rounded-lg bg-[#F6F1E6] p-2.5"><div className="flex items-center gap-1 text-[11px] text-zinc-500"><Clock className="h-3 w-3" />Best hour</div><div data-testid={`best-hour-${slug}`} className="font-display text-lg font-bold text-zinc-900">{hourLabel(a.bestHour)}–{hourLabel((a.bestHour + 1) % 24)}</div><div className="text-[11px] text-zinc-500">{a.hourAvg[a.bestHour].toLocaleString("en-IN")} avg views</div></div>
          </div>
          <div className="mt-3 flex h-20 items-end gap-1.5">
            {a.dayAvg.map((v, i) => (
              <div key={i} className="flex flex-1 flex-col items-center gap-1" title={`${DAYS[i]}: ${v.toLocaleString("en-IN")} avg views · ${a.dayN[i]} post(s)`}>
                <div className={`w-full rounded-t ${i === a.bestDay ? "bg-[#A9792C]" : "bg-zinc-200"}`} style={{ height: `${Math.max(3, (v / maxD) * 60)}px` }} />
                <span className="text-[10px] text-zinc-500">{DAYS[i][0]}</span>
              </div>
            ))}
          </div>
          <div className="mt-3 grid grid-cols-24 gap-px" style={{ gridTemplateColumns: "repeat(24, minmax(0, 1fr))" }}>
            {a.hourAvg.map((v, h) => <div key={h} title={`${hourLabel(h)}: ${v.toLocaleString("en-IN")} avg views · ${a.hourN[h]} post(s)`} className="h-5 rounded-sm" style={{ background: v ? `rgba(169,121,44,${0.15 + 0.85 * (v / maxH)})` : "#f4f4f5" }} />)}
          </div>
          <div className="mt-1 flex justify-between text-[10px] text-zinc-400"><span>12am</span><span>6am</span><span>12pm</span><span>6pm</span><span>11pm</span></div>
        </>
      )}
    </div>
  );
}

export function BestTime({ items, platforms }) {
  const pub = items.filter((t) => t.stage === "Published" && t.metrics);
  const used = platforms.filter((p) => pub.some((t) => (t.metrics || {})[p]));
  const enough = pub.length >= 5;
  return (
    <div data-testid="marketing-best-time" className="space-y-3">
      <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-3 text-xs text-zinc-600">
        Average views by the day and hour each post went live (India time). It uses the "Actual post time" on the post, or the time it was marked Published.
        {!enough && <span data-testid="best-time-low-data" className="ml-1 font-medium text-amber-700">Only {pub.length} post(s) have results so far. The picture gets reliable after about 5 to 10 posts per platform.</span>}
      </div>
      {used.length ? <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">{used.map((p) => <PlatformCard key={p} platform={p} a={analyse(pub, p)} />)}</div>
        : <div className="rounded-xl border border-dashed border-zinc-300 p-8 text-center text-sm text-zinc-400">Log views on published posts to see the best time to post.</div>}
    </div>
  );
}
